import { Menu, Transition } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { ChartBarIcon, EyeIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import { Fragment } from 'react'
import { useNavigate } from 'react-router-dom'

export type PageType = 'edit' | 'analysis' | 'review'

interface ProjectNavigationProps {
  projectId: string
  activePage: PageType
}

const pages = [
  {
    id: 'edit',
    name: 'Edit Project',
    icon: PencilSquareIcon,
    path: (id: string) => `/projects/${id}/edit`,
  },
  {
    id: 'analysis',
    name: 'Analysis',
    icon: ChartBarIcon,
    path: (id: string) => `/projects/${id}/analysis`,
  },
  {
    id: 'review',
    name: 'Annotation Review',
    icon: EyeIcon,
    path: (id: string) => `/projects/${id}/review`,
  },
] as const

export default function ProjectNavigation({ projectId, activePage }: ProjectNavigationProps) {
  const navigate = useNavigate()
  const currentPage = pages.find(p => p.id === activePage)

  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <Menu.Button className="inline-flex w-full justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
          <span className="flex items-center">
            {currentPage?.icon && <currentPage.icon className="mr-2 h-4 w-4" aria-hidden="true" />}
            {currentPage?.name}
          </span>
          <ChevronDownIcon className="-mr-1 h-5 w-5 text-gray-400" aria-hidden="true" />
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="py-1">
            {pages.map(page => (
              <Menu.Item key={page.id}>
                {({ active }) => (
                  <button
                    onClick={() => navigate(page.path(projectId))}
                    className={`
                      ${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'}
                      ${activePage === page.id ? 'bg-gray-50 font-bold' : ''}
                      group flex w-full items-center px-4 py-2 text-sm whitespace-nowrap
                    `}
                  >
                    <page.icon
                      className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                      aria-hidden="true"
                    />
                    {page.name}
                  </button>
                )}
              </Menu.Item>
            ))}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  )
}
